import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import eiReceiptService from '../../services/EIReceiptService.js';
import { ClipboardCheck, Pencil, ArrowLeft, Download, CheckCircle, XCircle, FolderOpen, DollarSign } from "lucide-react";
import { ROLES } from '../../utils/permissions.js';
import EIReceiptFileCabinet from './EIReceiptFileCabinet.js';

const ViewEIReceipt = () => {
  const { receiptId } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [showFileCabinet, setShowFileCabinet] = useState(false);
  const { user } = useAuth();

  const userRole = user?.role_name || user?.role;
  const isClient = userRole === ROLES.CLIENT;

  useEffect(() => {
    fetchReceipt();
  }, [receiptId]);

  const fetchReceipt = async () => {
    try {
      const response = await eiReceiptService.getById(receiptId);
      setReceipt(response.data);
    } catch (err) {
      Alert.error('Failed to load EI receipt details');
      console.error(err);
      navigate('/ei-receipts');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'paid': {
        bg: 'bg-green-100',
        text: 'text-green-700',
        label: 'Paid',
        icon: CheckCircle
      },
      'unpaid': {
        bg: 'bg-red-100',
        text: 'text-red-700',
        label: 'Unpaid',
        icon: XCircle
      },
      'pending': {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        label: 'Payment Pending',
        icon: DollarSign
      }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;

    return (
      <span className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const renderYesNo = (value) => {
    if (value === 'YES') {
      return (
        <span className="text-green-600 font-semibold flex items-center gap-1">
          <CheckCircle className="w-4 h-4" /> YES
        </span>
      );
    } else if (value === 'NOT SIGHTED') {
      return (
        <span className="text-yellow-600 font-semibold flex items-center gap-1">
          <XCircle className="w-4 h-4" /> NOT SIGHTED
        </span>
      );
    } else {
      return (
        <span className="text-red-600 font-semibold flex items-center gap-1">
          <XCircle className="w-4 h-4" /> NO
        </span>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading EI receipt details...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!receipt) {
    return null;
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <ClipboardCheck className="w-5 h-5 text-buttonBg" />
                      EI Receipt Details
                    </h1>
                  </div>
                  <button onClick={() => navigate('/ei-receipts')}
                    className="
                            relative overflow-hidden
                            group flex items-center justify-center gap-2
                            px-3 sm:px-3 py-2.5 sm:py-2
                            text-xs sm:text-sm
                            rounded-lg
                            border border-buttonBg
                            bg-buttonBg text-buttonTxt
                            font-medium whitespace-nowrap

                            transition-colors duration-300 ease-out
                            hover:bg-transparent hover:text-buttonBg
                          "
                        >
                          {/* COLLAPSING BACKGROUND */}
                          <span
                            className="
                              absolute inset-0
                              bg-buttonBg
                              scale-x-100
                              group-hover:scale-x-0
                              origin-center
                              transition-transform duration-300 ease-out
                              z-0
                            "
                          />

                          {/* ICON */}
                          <ArrowLeft
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />

                          <span className="relative z-10">Back to EI Reciept</span>
                  </button>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-sm shadow-sm overflow-hidden">
                  {/* Profile Header Section */}
                  <div className="bg-primary p-3 px-4">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 lg:gap-4">
                      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-3">
                        <h2 className="text-xl sm:text-lg md:text-lg font-semibold text-secondary mb-0 break-words">
                          {receipt.ei_receipt_id}
                        </h2>
                        {!isClient && (
                          <div className="flex flex-wrap items-center gap-2">
                            {receipt.send_to_dm && (
                              <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                <CheckCircle className="w-3 h-3" />
                                Sent to Documentation Manager
                              </span>
                            )}
                            {receipt.published && (
                              <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3" />
                                Published to Client
                              </span>
                            )}
                            {!receipt.send_to_dm && !receipt.published && (
                              <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                <XCircle className="w-3 h-3" />
                                Draft
                              </span>
                            )}
                            {receipt.payment_status && getPaymentStatusBadge(receipt.payment_status)}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <button
                          onClick={() => setShowFileCabinet(true)}
                          className="
                        relative overflow-hidden
                        group flex items-center justify-center gap-2
                        px-3 sm:px-5 py-2.5 sm:py-2
                        text-xs sm:text-sm
                        rounded-lg
                        border border-buttonBg
                        bg-white text-buttonBg
                        font-medium whitespace-nowrap

                        transition-colors duration-300 ease-out
                        hover:text-white
                      "
                    >
                      {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                      <span
                        className="
                          absolute inset-0
                          bg-buttonBg
                          scale-x-0
                          group-hover:scale-x-100
                          origin-center
                          transition-transform duration-300 ease-out
                          z-0
                        "
                      />
					  {/* ICON */}
                          <FolderOpen
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />
                      <span className="relative z-10">File Cabinet</span>
                          
                        </button>
                        {!isClient && (
                          <button
                            onClick={() => navigate(`/ei-receipts/edit/${receiptId}`)}
                            className="
                        relative overflow-hidden
                        group flex items-center justify-center gap-2
                        px-3 sm:px-5 py-2.5 sm:py-2
                        text-xs sm:text-sm
                        rounded-lg
                        border border-buttonBg
                        bg-white text-buttonBg
                        font-medium whitespace-nowrap

                        transition-colors duration-300 ease-out
                        hover:text-white
                      "
                    >
                      {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                      <span
                        className="
                          absolute inset-0
                          bg-buttonBg
                          scale-x-0
                          group-hover:scale-x-100
                          origin-center
                          transition-transform duration-300 ease-out
                          z-0
                        "
                      />
					  {/* ICON */}
                          <Pencil
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />
                      <span className="relative z-10">Edit Receipt</span>
                            
                          </button>
                        )}
                        <button
                            onClick={async () => {
                              if (isClient && !receipt.published) return;
                              try {
                                const response = await eiReceiptService.downloadPDF(receiptId);
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', `EI_Receipt_${(receipt.tank_no || receipt.ei_receipt_id).replace(/\//g, '_')}.pdf`);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                window.URL.revokeObjectURL(url);
                                Alert.success('PDF downloaded successfully');
                              } catch (err) {
                                Alert.error('Failed to download PDF');
                                console.error(err);
                              }
                            }}
                            disabled={isClient && !receipt.published}
                            title={isClient && !receipt.published ? 'PDF download is only available after publishing' : ''}
                            className={`relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-white text-buttonBg font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:text-white ${isClient && !receipt.published ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                      {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                      <span
                        className="
                          absolute inset-0
                          bg-buttonBg
                          scale-x-0
                          group-hover:scale-x-100
                          origin-center
                          transition-transform duration-300 ease-out
                          z-0
                        "
                      />
					  {/* ICON */}
                          <Download
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />
                      <span className="relative z-10">Download PDF</span>
                          
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Details Sections */}
                  <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
                    {/* Basic Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">EI Receipt ID</label>
                          <span className={`inline-block text-sm sm:text-base font-semibold px-0 py-1 rounded-lg ${
                            !isClient 
                              ? receipt.published 
                                ? 'text-secondary' 
                                : 'text-secondary'
                              : 'text-secondary'
                          }`}>
                            {receipt.ei_receipt_id}
                          </span>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Survey Date</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(receipt.date_of_survey)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Inspector</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{receipt.inspected_by_name}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Last Cargo Carried</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{receipt.last_cargo_carried || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">CHA</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{receipt.cha || 'N/A'}</p>
                        </div>
                        {receipt.location && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Location</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                              {receipt.location.city}, {receipt.location.state}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Seal Numbers */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Seal Numbers
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Manlid Seal No</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{receipt.manlid_seal_no || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Airline V/Seal No</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{receipt.airline_v_seal_no || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Bottom Seal No</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{receipt.bottom_seal_no || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Top Discharge Seal No</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{receipt.top_discharge_seal_no || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Tank Details */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Tank Details
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Tank Container No</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{receipt.tank_no || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Type</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{receipt.tank_type || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Max Gross Wt.</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{receipt.maximum_gross_weight || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Tare Wt.</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{receipt.tare_weight || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Capacity</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{receipt.capacity_ltr || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">C.S.C</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{receipt.csc || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Depot Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{receipt.depot_name || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Manufacturing Date</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(receipt.manufacture_date)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Next Test Date</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(receipt.ned)}</p>
                        </div>
                      </div>
                    </div>

                    {/* General Condition */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        General Condition
                      </h3>
                      <div className="space-y-3">
                        {receipt.general_condition && Object.entries({
                          frame_tank_walkways_free: 'Frame, Tank and walkways free of contamination and cargo',
                          manlid_valve_compartments_free: 'Manlid and valve compartments free of contamination and cargo',
                          serial_numbers_legible: 'Serial numbers and statutory marking legible',
                          unit_having_steam_jacket: 'Unit having steam jacket',
                          unit_having_bottom_seal: 'Unit having bottom seal provision for bullet seal',
                          unit_having_gasket_on_manlid: 'Unit having gasket on manlid cover',
                          dipstick_originally_fitted: 'Dipstick originally fitted',
                          calibration_chart_fitted: 'Calibration chart fitted',
                          siphon_tube_originally_fitted: 'Siphon tube originally fitted',
                          no_cuts_on_ladder_and_walkway: 'No cuts on ladder and walkway',
                          pressure_gauge_fitted: 'Pressure gauge fitted',
                          temperature_gauge_fitted: 'Temperature gauge fitted',
                          prv_with_popset: 'PRV with Poppet',
                          top_discharge_provision: 'Top discharge provision',
                          rfid_gps_unit: 'RFID GPS unit',
                          interior_clean_dry_and_odourless: 'Interior clean dry and odourless',
                          valves_fittings_free_from_previous_cargo: 'VALVES/FITTINGS free from previous cargo'
                        }).map(([key, label]) => (
                          <div key={key} className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-0.5">
                              {renderYesNo(receipt.general_condition[key])}
                            </div>
                            <span className="text-sm sm:text-base text-secondary">{label}</span>
                          </div>
                        ))}
                        {receipt.general_condition?.walk_way_type && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Walk way type</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold">{receipt.general_condition.walk_way_type}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Internal Remarks */}
                    {receipt.internal_remarks && (
                    <div>
                        <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Internal Remarks
                        </h3>
                        <div className="space-y-4">
                        {/* Specific Fields */}
                        {receipt.internal_remarks.non_transferable_cargo_stains && (
                            <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">
                                Non transferable cargo stains on interior shell:
                            </label>
                            <p className="text-sm sm:text-base text-secondary font-semibold">
                                {receipt.internal_remarks.non_transferable_cargo_stains}
                            </p>
                            </div>
                        )}

                        {receipt.internal_remarks.polish_buffing_marks && (
                            <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">
                                Polish/Buffing marks in interior:
                            </label>
                            <p className="text-sm sm:text-base text-secondary font-semibold">
                                {receipt.internal_remarks.polish_buffing_marks}
                            </p>
                            </div>
                        )}

                        {/* Additional Remarks */}
                        {receipt.internal_remarks.additional_remarks && receipt.internal_remarks.additional_remarks.length > 0 && (
                            <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
                                Additional Internal Remarks
                            </label>
                            <ul className="space-y-2">
                                {receipt.internal_remarks.additional_remarks.map((remark, index) => (
                                <li key={index} className="text-sm sm:text-base text-secondary flex items-start gap-2">
                                    <span className="text-orange-500 font-bold">•</span>
                                    <span>{remark}</span>
                                </li>
                                ))}
                            </ul>
                            </div>
                        )}
                        </div>
                    </div>
                    )}

                    {/* Remarks */}
                    {receipt.remarks && receipt.remarks.length > 0 && (
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                          Remarks
                        </h3>
                        <ul className="space-y-2">
                          {receipt.remarks.map((remark, index) => (
                            <li key={index} className="text-sm sm:text-base text-secondary flex items-start gap-2">
                              <span className="text-orange-500 font-bold">•</span>
                              <span>{remark}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* System Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created By</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{receipt.created_by || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created At</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(receipt.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Last Updated</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(receipt.updated_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Published Status</label>
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                            receipt.published
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {receipt.published ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            {receipt.published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
      {/* File Cabinet Modal */}
      {showFileCabinet && receipt && (
        <EIReceiptFileCabinet 
          eiReceiptId={receipt.ei_receipt_id} 
          clientId={receipt.client_id} 
          onClose={() => setShowFileCabinet(false)} 
        />
      )}
    </div>
  );
};

export default ViewEIReceipt;